import { protectedPagesExplore, protectedPagesHub, type Page } from '@/constants/routes';
import { i18nConfig } from '@/i18n/i18nConfig';
import { filterHubNavPages, type HubNavFlags } from '@/lib/nav/filterHubNavPages';

/**
 * A page the assistant may open. This list is sent to the agent as `context.navigation.pages`
 * (skywatch-agent ADR 0023), so the agent offers exactly these pages, and the same list allowlists
 * the agent's navigate actions. To add or remove a page, change the nav routes or AGENT_EXTRA_PAGES.
 */
export interface AgentNavPage {
  key: string;
  label: string;
  path: string;
  description?: string;
  /** Detail route with an `{id}` slot; lets the agent open one item on this page. */
  detailPath?: string;
}

export type AgentNavApp = 'hub' | 'explore';

// Pages outside the main nav that the assistant can still open.
const AGENT_EXTRA_PAGES: Record<AgentNavApp, AgentNavPage[]> = {
  hub: [
    {
      key: 'my_account',
      label: 'My Account',
      path: '/my-account',
      description: 'profile and API keys',
    },
    { key: 'cart', label: 'Cart', path: '/cart', description: 'imagery added to the cart' },
  ],
  explore: [
    {
      key: 'my_account',
      label: 'My Account',
      path: '/my-account',
      description: 'profile and API keys',
    },
    { key: 'cart', label: 'Cart', path: '/cart', description: 'imagery added to the cart' },
  ],
};

const PAGE_DETAILS: Record<string, Pick<AgentNavPage, 'description' | 'detailPath'>> = {
  '/': { description: 'search the archive and order imagery' },
  '/projects': { description: 'project list', detailPath: '/projects/{id}' },
  '/data-management': { description: 'your orders and deliveries' },
  '/my-orders': { description: 'your orders and deliveries' },
};

const ENTITY_ID = /^[A-Za-z0-9_-]{1,64}$/;

function toSnakeKey(tkey: string): string {
  return tkey.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function toAgentNavPage(page: Page): AgentNavPage {
  return {
    key: toSnakeKey(page.tkey),
    label: page.title,
    path: page.href,
    ...PAGE_DETAILS[page.href],
  };
}

/**
 * The pages the assistant may open for this app. Hub pages follow the same visibility rules as the
 * header nav; with no flags yet (profile still loading) every Hub nav page is listed.
 */
export function buildAgentNavPages(app: AgentNavApp, flags?: HubNavFlags): AgentNavPage[] {
  const navPages =
    app === 'hub'
      ? flags
        ? filterHubNavPages(protectedPagesHub, flags)
        : protectedPagesHub
      : protectedPagesExplore;
  return [...navPages.map(toAgentNavPage), ...AGENT_EXTRA_PAGES[app]];
}

function isDeclaredPath(path: string, pages: AgentNavPage[]): boolean {
  return pages.some(page => {
    if (page.path === path) return true;
    if (!page.detailPath) return false;
    const [prefix, suffix] = page.detailPath.split('{id}');
    if (!path.startsWith(prefix) || !path.endsWith(suffix)) return false;
    return ENTITY_ID.test(path.slice(prefix.length, path.length - suffix.length));
  });
}

/**
 * Turns a path from an agent navigate action into the URL to push, or null when the page is not
 * one the assistant may open. The default locale is served unprefixed, so only a real non-default
 * locale taken from the current URL is kept in front of the path.
 */
export function resolveAgentNavigatePath(
  path: unknown,
  currentPathname: string,
  pages: AgentNavPage[]
): string | null {
  if (typeof path !== 'string' || !isDeclaredPath(path, pages)) return null;

  const firstSegment = currentPathname.split('/')[1] ?? '';
  const isPrefixedLocale =
    firstSegment !== i18nConfig.defaultLocale &&
    (i18nConfig.locales as string[]).includes(firstSegment);
  if (!isPrefixedLocale) return path;
  return path === '/' ? `/${firstSegment}` : `/${firstSegment}${path}`;
}
